// 2026-08-23 auth — Supabase SSR, httpOnly cookies, secure, sameSite lax
// 2026-09-02 Sentry user context — setUser({id}) after auth, clear on logout (websearch 2026 Sentry Next.js + Supabase)
"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import type { AuthChangeEvent, Session, User } from "@supabase/supabase-js";
import type { SupabaseClient } from "@supabase/supabase-js";
import { logger } from "@/lib/logger";

// 2026-10-10 E3: `@/lib/supabase/client` era un import estático y arrastraba el
// SDK cliente completo (@supabase/supabase-js + @supabase/ssr) al first-load
// de las 38 rutas — el chunk compartido pesaba 1259 KB. Con import() diferido
// el SDK vive en su propio chunk y solo se pide cuando el AuthProvider
// arranca; el visitante anónimo del landing nunca lo descarga.
let supabasePromise: Promise<SupabaseClient | null> | null = null;

/** Cliente Supabase bajo demanda (null si el entorno no está configurado). */
function loadSupabase(): Promise<SupabaseClient | null> {
  supabasePromise ??= import("@/lib/supabase/client").then(
    ({ createClient, isSupabaseConfigured }) =>
      isSupabaseConfigured() ? createClient() : null,
  );
  return supabasePromise;
}

// Sentry optional — dynamic so build does not fail when DSN missing
async function syncSentryUser(user: User | null) {
  try {
    const Sentry = (await import("@sentry/nextjs").catch(
      () => null,
    )) as unknown as {
      setUser?: (u: { id: string } | null) => void;
    } | null;
    if (!Sentry?.setUser) return;
    if (user?.id) Sentry.setUser({ id: user.id });
    else Sentry.setUser(null);
  } catch {}
}

interface AuthContextType {
  user: User | null;
  isAdmin: boolean;
  plan: string | null;
  subscriptionStatus: string | null;
  hasPassword: boolean;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isAdmin: false,
  plan: null,
  subscriptionStatus: null,
  hasPassword: false,
  loading: true,
  signOut: async () => {},
  refreshUser: async () => {},
});

function clearSupabaseAuthCookies() {
  if (typeof document === "undefined") return;

  for (const cookie of document.cookie.split(";")) {
    const name = cookie.split("=")[0]?.trim();
    if (!name?.startsWith("sb-")) continue;
    document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax`;
  }
}

// ── Client-side dedup cache for /api/auth/me ──
// Multiple components and event handlers fire fetchMe() simultaneously.
// This ensures at most one in-flight request per 30 s window (2026-09-14 P2 CPU
// budget, AGENTS.md Free-tier: one owner per poll + module-level dedup ≥30s con
// shared in-flight — mismo patrón fetchUsageShared en use-chat-quotas.ts:48).
const ME_CACHE_TTL_MS = 30_000;
let meCachePromise: Promise<{
  ok: boolean;
  data?: {
    isAdmin: boolean;
    plan: string | null;
    subscriptionStatus: string | null;
  };
}> | null = null;
let meCacheTime = 0;

function dedupFetchMe(): Promise<{
  ok: boolean;
  data?: {
    isAdmin: boolean;
    plan: string | null;
    subscriptionStatus: string | null;
  };
}> {
  const now = Date.now();
  if (meCachePromise && now - meCacheTime < ME_CACHE_TTL_MS) {
    return meCachePromise;
  }
  meCacheTime = now;
  // RESUELTO 2026-09-13 [B-P2] no-store OK: /api/auth/me es server no-store (auth sensible, fuera de los 5 cacheables) + dedup 30s |fuente src/app/api/auth/me/route.ts:18
  // 2026-09-14 P2: TTL 5s→30s — /api/auth/me plan-check tiene techo ≥15min
  // (AGENTS.md Free-tier polling ceilings); 30s es el mínimo del dedup
  // compartido y el no-store sigue justificado (dato auth sensible, no uno de
  // los 5 endpoints cacheables). refreshUser manual usa force implícito vía
  // expiración natural, no bypass.
  // one-shot con frescura justificada: dedup 30s sin setInterval (sin poller propio), exento de regla interval-cache.
  meCachePromise = fetch("/api/auth/me", { cache: "no-store" })
    .then(async (res) => {
      if (res.ok) {
        const data = (await res.json()) as {
          isAdmin: boolean;
          plan: string | null;
          subscriptionStatus: string | null;
        };
        return { ok: true, data };
      }
      return { ok: false };
    })
    .catch(() => ({ ok: false as const }));
  return meCachePromise;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const supabaseRef = useRef<SupabaseClient | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [plan, setPlan] = useState<string | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(
    null,
  );
  const [hasPassword, setHasPassword] = useState(false);
  // E3: sin `isSupabaseConfigured()` síncrono — el estado se resuelve cuando
  // el cliente diferido contesta (un tick), igual que antes con cookies.
  const [loading, setLoading] = useState(true);

  const resetAuthState = useCallback(() => {
    setIsAdmin(false);
    setPlan(null);
    setSubscriptionStatus(null);
    void syncSentryUser(null);
  }, []);

  const updateSessionUser = useCallback((newUser: User | null) => {
    setUser((prev) => {
      if (prev?.id === newUser?.id) return prev;
      // Sync Sentry user context per-request isolation — GDPR: only id, no email (server beforeSend also strips)
      void syncSentryUser(newUser);
      return newUser;
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    let unsubscribe: (() => void) | undefined;

    void (async () => {
      let supabase: SupabaseClient | null;
      try {
        supabase = await loadSupabase();
      } catch (err) {
        if (!cancelled) setLoading(false);
        if (process.env.NODE_ENV !== "production")
          logger.error("[auth-context] supabase client failed:", {
            error: String(err),
          });
        return;
      }
      if (cancelled) return;
      if (!supabase) {
        setLoading(false);
        return;
      }
      supabaseRef.current = supabase;

      const fetchMe = async () => {
        try {
          const result = await dedupFetchMe();
          if (cancelled) return;
          if (result.ok && result.data) {
            setIsAdmin(result.data.isAdmin ?? false);
            setPlan(result.data.plan ?? null);
            setSubscriptionStatus(result.data.subscriptionStatus ?? null);
          } else if (!result.ok) {
            resetAuthState();
          }
        } catch (err) {
          if (!cancelled) {
            resetAuthState();
          }
          if (process.env.NODE_ENV !== "production")
            logger.error("[auth-context] fetchMe failed:", {
              error: String(err),
            });
        }
      };

      const fetchSession = async () => {
        try {
          const sessionPromise = supabase.auth.getSession();
          const mePromise = dedupFetchMe();

          const [{ data, error }, result] = await Promise.all([
            sessionPromise,
            mePromise,
          ]);
          if (cancelled) return;

          if (error) throw error;
          const { session } = data;
          const currentUser = session?.user ?? null;
          updateSessionUser(currentUser);
          setHasPassword(
            currentUser?.identities?.some(
              (identity) => identity.provider === "email",
            ) ?? false,
          );

          if (result.ok && result.data) {
            setIsAdmin(result.data.isAdmin ?? false);
            setPlan(result.data.plan ?? null);
            setSubscriptionStatus(result.data.subscriptionStatus ?? null);
          } else {
            resetAuthState();
          }
          setLoading(false);
        } catch (err) {
          if (!cancelled) setLoading(false);
          if (process.env.NODE_ENV !== "production")
            logger.error("[auth-context] getSession failed:", {
              error: String(err),
            });
        }
      };

      await fetchSession();
      if (cancelled) return;

      // onAuthStateChange handles real auth changes (sign-in, sign-out, token refresh).
      // fetchMe() is deduped, so multiple rapid calls share one request.
      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange(
        (_event: AuthChangeEvent, session: Session | null) => {
          if (cancelled) return;
          const currentUser = session?.user ?? null;
          updateSessionUser(currentUser);
          setHasPassword(
            currentUser?.identities?.some(
              (identity) => identity.provider === "email",
            ) ?? false,
          );
          if (currentUser) {
            fetchMe();
          } else {
            resetAuthState();
          }
        },
      );
      unsubscribe = () => subscription.unsubscribe();
    })();

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [resetAuthState, updateSessionUser]);

  const signOut = useCallback(async () => {
    let supabase: SupabaseClient | null;
    try {
      supabase = supabaseRef.current ?? (await loadSupabase());
    } catch {
      supabase = null;
    }
    if (!supabase) {
      setUser(null);
      resetAuthState();
      void syncSentryUser(null);
      setLoading(false);
      return;
    }

    const { error } = await supabase.auth.signOut();
    if (error) {
      if (process.env.NODE_ENV !== "production") {
        logger.error("[auth-context] signOut failed:", {
          error: String(error),
        });
      }
    }
    clearSupabaseAuthCookies();
    setUser(null);
    resetAuthState();
    void syncSentryUser(null);
    setLoading(false);
  }, [resetAuthState]);

  const refreshUser = useCallback(async () => {
    let supabase: SupabaseClient | null;
    try {
      supabase = supabaseRef.current ?? (await loadSupabase());
    } catch {
      supabase = null;
    }
    if (!supabase) {
      setUser(null);
      resetAuthState();
      setHasPassword(false);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase.auth.getSession();
      if (error) throw error;
      const currentUser = data.session?.user ?? null;
      updateSessionUser(currentUser);
      setHasPassword(
        currentUser?.identities?.some(
          (identity) => identity.provider === "email",
        ) ?? false,
      );
      if (currentUser) {
        // Use deduped fetch — won't fire a duplicate request if one is already in-flight
        const result = await dedupFetchMe();
        if (result.ok && result.data) {
          setIsAdmin(result.data.isAdmin ?? false);
          setPlan(result.data.plan ?? null);
          setSubscriptionStatus(result.data.subscriptionStatus ?? null);
        }
      } else {
        resetAuthState();
      }
    } catch (err) {
      if (process.env.NODE_ENV !== "production")
        logger.error("[auth-context] refreshUser failed:", {
          error: String(err),
        });
    }
  }, [resetAuthState, updateSessionUser]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAdmin,
        plan,
        subscriptionStatus,
        hasPassword,
        loading,
        signOut,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
